import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import Chart from "react-apexcharts";
import html2canvas from "html2canvas";
import { Document, pdf, PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import CompanyRetirementDataPDF from "./pdf/CompanyRetirementDataPDF";
import { Tooltip } from "react-tooltip";
import { useSelector } from "react-redux";
import { API_URL } from "../config/host";
import Button from "../components/form/Button";
import { t } from "i18next";
import { postData } from "../services/api";

const CompanyReadinessTimeframeReport = () => {
  const chartRef = useRef(null);
  const [chartImage, setChartImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [retirementWindow, setRetirementWindow] = useState([]);

  const user = useSelector((state) => state.auth?.user);

  // Dynamic color palette for different retirement windows
  const getColorPalette = (index, total) => {
    const colors = [
      {
        primary: "#ff2d55",
        gradient: "from-[#ff2d55] to-[#ffc1cc]",
        bg: "from-red-200 to-rose-100",
        border: "border-[#ffc1cc]",
        text: "text-[#ff2d55]",
        icon: "fa-solid fa-circle-xmark",
      },
      {
        primary: "#fbbf24",
        gradient: "from-[#fbbf24] to-[#fff4cc]",
        bg: "from-yellow-200 to-amber-100",
        border: "border-[#fff4cc]",
        text: "text-[#fbbf24]",
        icon: "fa-solid fa-chart-line",
      },
      {
        primary: "#10b981",
        gradient: "from-[#10b981] to-[#d3f4e1]",
        bg: "from-green-200 to-emerald-100",
        border: "border-[#d3f4e1]",
        text: "text-[#10b981]",
        icon: "fa-solid fa-calendar-days",
      },
      {
        primary: "#3b82f6",
        gradient: "from-[#3b82f6] to-[#dceeff]",
        bg: "from-blue-200 to-sky-100",
        border: "border-[#dceeff]",
        text: "text-[#3b82f6]",
        icon: "fa-solid fa-users-between-lines",
      },
      {
        primary: "#9ca3af",
        gradient: "from-[#9ca3af] to-[#e2e4e9]",
        bg: "from-slate-200 to-slate-100",
        border: "border-[#e2e8f0]",
        text: "text-[#9ca3af]",
        icon: "fa-solid fa-question-circle",
      },
      {
        primary: "#8b5cf6",
        gradient: "from-[#8b5cf6] to-[#e9d5ff]",
        bg: "from-purple-200 to-violet-100",
        border: "border-[#e9d5ff]",
        text: "text-[#8b5cf6]",
        icon: "fa-solid fa-clock",
      },
      {
        primary: "#f59e0b",
        gradient: "from-[#f59e0b] to-[#fef3c7]",
        bg: "from-orange-200 to-yellow-100",
        border: "border-[#fef3c7]",
        text: "text-[#f59e0b]",
        icon: "fa-solid fa-briefcase",
      },
    ];

    return colors[index % colors.length];
  };

  // Configuration for different retirement window categories
  const retirementConfig = {
    yearThresholds: [
      {
        max: 2,
        urgency: "High Risk",
        icon: "fa-solid fa-exclamation-triangle",
      },
      { max: 5, urgency: "High Priority", icon: "fa-solid fa-chart-line" },
      { max: 9, urgency: "Preferred Range", icon: "fa-solid fa-calendar-days" },
      {
        max: 15,
        urgency: "Stable Candidate",
        icon: "fa-solid fa-users-between-lines",
      },
      {
        max: Infinity,
        urgency: "Long Term Stable",
        icon: "fa-solid fa-shield-alt",
      },
    ],

    // Special keyword mappings (case-insensitive)
    specialKeywords: {
      eliminates: {
        urgency: "Eliminates Candidate",
        icon: "fa-solid fa-circle-xmark",
      },
      unknown: { urgency: "Needs Review", icon: "fa-solid fa-question-circle" },
      "not specified": {
        urgency: "Needs Review",
        icon: "fa-solid fa-question-circle",
      },
      "n/a": { urgency: "Needs Review", icon: "fa-solid fa-question-circle" },
      immediate: { urgency: "Immediate Action", icon: "fa-solid fa-bolt" },
      critical: {
        urgency: "Critical Priority",
        icon: "fa-solid fa-exclamation",
      },
      low: { urgency: "Low Priority", icon: "fa-solid fa-clock" },
    },

    // Fallback for unmatched cases
    fallback: { urgency: "Standard", icon: "fa-solid fa-user" },
  };

  // Helper function to extract numeric value from retirement window name
  const extractYearsFromName = (name) => {
    const lowerName = name.toLowerCase().trim();

    // Extract numbers from the string
    const numbers = lowerName.match(/\d+/g);
    if (!numbers) return null;

    // Convert to numbers and find the average for ranges
    const nums = numbers.map((num) => parseInt(num));

    if (nums.length === 1) {
      return nums[0];
    } else if (nums.length === 2) {
      // For ranges like "3-5" or "15-20", return the average
      return (nums[0] + nums[1]) / 2;
    }

    return nums[0]; // fallback to first number
  };

  // Dynamic urgency and icon assignment based on configuration
  const getUrgencyAndIcon = (window) => {
    const lowerName = window.name.toLowerCase().trim();

    // First check if window fails the pass condition
    if (!window.isPass) {
      return retirementConfig.specialKeywords["eliminates"];
    }

    // Check for special keywords first
    for (const [keyword, config] of Object.entries(
      retirementConfig.specialKeywords
    )) {
      if (lowerName.includes(keyword)) {
        return config;
      }
    }

    // Then check year-based thresholds
    const years = extractYearsFromName(window.name);
    if (years !== null) {
      for (const threshold of retirementConfig.yearThresholds) {
        if (years <= threshold.max) {
          return {
            urgency: threshold.urgency,
            icon: threshold.icon,
          };
        }
      }
    }

    // Return fallback if nothing matches
    return retirementConfig.fallback;
  };

  // Generate dynamic window configs based on retirementWindow data
  const generateWindowConfigs = useCallback(() => {
    if (!retirementWindow || retirementWindow.length === 0) return [];

    // Sort by ordering field to maintain consistent order
    const sortedWindows = [...retirementWindow].sort(
      (a, b) => (a.ordering || 0) - (b.ordering || 0)
    );

    return sortedWindows.map((window, index) => {
      const colorConfig = getColorPalette(index, sortedWindows.length);
      const { urgency, icon } = getUrgencyAndIcon(window);

      return {
        _id: window._id,
        key: window.name,
        title: window.name,
        icon: icon,
        urgency: urgency,
        gradient: `bg-gradient-to-br ${colorConfig.gradient}`,
        bg: colorConfig.bg,
        border: colorConfig.border,
        text: colorConfig.text,
        primary: colorConfig.primary,
        isPass: window.isPass,
        ordering: window.ordering || index,
        point: window.point,
        years: extractYearsFromName(window.name), // Store extracted years for future use
      };
    });
  }, [retirementWindow]);

  const windowConfigs = generateWindowConfigs();

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await postData("filter-item/list", {
        sortQuery: { created_at: 1 },
        type: "timeframe",
        status: 1,
      });

      if (result.data) {
        setRetirementWindow(result.data);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const loadRetirementList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await postData("/readiness-overview", {
        isReport: 1,
      });

      if (result?.data) {
        const retirementData = result?.data?.map((item) => ({
          _id: item._id,
          name: item.name,
          position: item?.position?.name || "Unknown Position",
          window: item.timeframe_details?.name || "Unknown",
          windowId: item.timeframe_details?._id || null,
        }));
        setList(retirementData);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRetirementList();
  }, [loadRetirementList]);

  // Dynamic groupedData based on actual retirement windows
  const groupedData = useMemo(() => {
    if (!list || list.length === 0) return {};

    return list.reduce((acc, person) => {
      const windowKey = person.window || "Unknown";
      if (!acc[windowKey]) acc[windowKey] = [];
      acc[windowKey].push(person);
      return acc;
    }, {});
  }, [list]);

  // Dynamic chart series based on window configs
  const chartSeries = useMemo(() => {
    return windowConfigs.map((config) => groupedData[config.key]?.length || 0);
  }, [windowConfigs, groupedData]);

  // Dynamic chart labels and colors
  const chartLabels = useMemo(() => {
    return windowConfigs.map((config) => config.title);
  }, [windowConfigs]);

  const chartColors = useMemo(() => {
    return windowConfigs.map((config) => config.primary);
  }, [windowConfigs]);

  const pieChartOptions = (title, labels, colors) => ({
    chart: {
      type: "pie",
    },
    labels,
    colors,
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        const value = opts.w.config.series[opts.seriesIndex];
        return `${value}`;
      },
      style: {
        fontSize: "14px",
        fontWeight: "semibold",
        align: "center",
        colors: ["#000"],
      },
      dropShadow: {
        enabled: true,
        blur: 3,
        opacity: 0.5,
      },
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return `${val} employees`;
        },
      },
    },
    title: {
      text: title,
      align: "center",
      style: {
        fontSize: "20px",
        fontWeight: "bold",
      },
    },
    legend: {
      show: false,
    },
  });

  // Dynamic custom legend
  const customLegend = useMemo(() => {
    return windowConfigs.map((config, index) => ({
      label: config.title,
      color: config.primary,
      value: chartSeries[index] || 0,
    }));
  }, [windowConfigs, chartSeries]);

  // useEffect(() => {
  //   if (chartRef.current && chartSeries.length > 0) {
  //     setTimeout(() => {
  //       html2canvas(chartRef.current).then((canvas) => {
  //         const imgData = canvas.toDataURL("image/png");
  //         setChartImage(imgData);
  //       });
  //     }, 1000);
  //   }
  // }, [chartSeries]);

  useEffect(() => {
    if (chartRef.current && chartSeries.length > 0) {
      setTimeout(() => {
        html2canvas(chartRef.current, {
          scale: 2, // increase pixel density for sharper image
          useCORS: true, // if chart loads external images
        }).then((canvas) => {
          const imgData = canvas.toDataURL("image/png");
          setChartImage(imgData);
        });
      }, 1000);
    }
  }, [chartSeries]);

  const handleDownloadClick = async (e) => {
    e.preventDefault();
    const newTab = window.open("", "_blank");

    // Display a styled loading message with Tailwind CSS.
    newTab.document.write(`
      <html lang="en">
        <head>
          <script src="https://cdn.tailwindcss.com"></script>
          <style>
            body {
              @apply bg-gray-100;
              margin: 0;
              height: 100vh;
            }
            .loader-container {
              @apply flex flex-col justify-center items-center h-screen;
            }
            .loader {
              width: 50px;
              aspect-ratio: 1;
              display: grid;
              border-radius: 50%;
              background:
                linear-gradient(0deg, rgb(0 0 0 / 50%) 30%, #0000 0 70%, rgb(0 0 0 / 100%) 0) 50% / 8% 100%,
                linear-gradient(90deg, rgb(0 0 0 / 25%) 30%, #0000 0 70%, rgb(0 0 0 / 75%) 0) 50% / 100% 8%;
              background-repeat: no-repeat;
              animation: l23 1s infinite steps(12);
            }
            .loader::before,
            .loader::after {
              content: "";
              grid-area: 1 / 1;
              border-radius: 50%;
              background: inherit;
              opacity: 0.915;
              transform: rotate(30deg);
            }
            .loader::after {
              opacity: 0.83;
              transform: rotate(60deg);
            }
            @keyframes l23 {
              100% {
                transform: rotate(1turn);
              }
            }
          </style>
        </head>
        <body>
          <div class="flex justify-center items-center mt-20 loader-container">
          <div class="flex flex-col items-center justify-center space-y-4">
            <div class="loader"></div>
            <p class="text-teal-700 text-lg font-semibold mt-4">Generating your PDF... Please wait.</p>
          </div>
         </div>
        </body>
      </html>
      `);
    try {
      // Generate the PDF blob asynchronously.
      const blob = await pdf(
        <CompanyRetirementDataPDF
          user={user}
          companyLogo={API_URL + user?.image}
          employees={list}
          chartImageUrl={chartImage}
          groups={retirementWindow}
          title={"Readiness Overview"}
          pageTitle={"Company Readiness Breakdown Report"}
          pageSubTitle={"Candidate"}
          customLegend={customLegend}
        />
      ).toBlob();

      // Create a URL from the blob.
      const blobURL = URL.createObjectURL(blob);
      // Redirect the new tab to the blob URL (load the PDF).
      newTab.location.href = blobURL;
    } catch (error) {
      // Display an error message in the new tab.
      newTab.document.body.innerHTML = `
        <div class="flex flex-col items-center justify-center h-screen bg-red-100">
          <p class="text-red-700 text-lg font-semibold">Failed to load PDF. Please try again later.</p>
          <p class="text-sm text-gray-600">${error.message}</p>
        </div>
      `;
      console.error("PDF generation error:", error);
    }
  };

  // Show loading state if data is not ready
  if (loading || windowConfigs.length === 0) {
    return (
      <div className="border bg-white border-gray-200 p-4 shadow-md rounded-xl">
        {/* <div className="flex justify-center items-center h-64">
          <div className="text-slate-500">Loading readiness data...</div>
        </div> */}
        <div className="flex flex-col items-center justify-center py-16 text-gray-500 text-sm">
          <div className="mb-4">
            <i className="fa fa-spinner fa-spin text-teal-600 text-5xl"></i>
          </div>
          <p className="mt-4 text-teal-600 font-semibold text-lg">
            Loading readiness data ...
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Please wait while we fetch the data
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="border bg-white border-gray-200 p-4 shadow-md rounded-xl">
        <div className="relative flex justify-between items-center mb-3 gap-4">
          <div className="mb-6">
            <div className="text-xl md:text-3xl text-slate-800 font-bold">
              {user?.company_name} Readiness Breakdown
            </div>
            <div className="text-sm text-slate-500 mt-1">
              Visual & detailed breakdown of each readiness timeframe
            </div>
          </div>

          {chartImage && (
            <button
              type="button"
              onClick={handleDownloadClick}
              className="
                flex justify-center items-center gap-2
                border border-teal-400 text-teal-500
                rounded-full text-sm h-8 px-4
                bg-transparent font-semibold
                transition-all duration-200
                hover:bg-teal-50 hover:text-teal-700
                active:scale-[0.97]
                focus:outline-none focus:ring-2 focus:ring-teal-400/40
              "
            >
              <i className="fa-fw fa-regular fa-download text-sm"></i>
              <span className="text-xs font-semibold whitespace-nowrap">
                {t('download_report')}
              </span>
            </button>
          )}
        </div>

        <div className="w-full rounded-md shadow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="px-4 py-4 bg-white p-3 rounded-xl shadow border border-slate-200 w-full max-w-full">
              <div
                ref={chartRef}
                className="relative"
              >
                <div className="text-center mb-6">
                  <h3 className="text-xl md:text-4xl font-bold text-gray-800">
                    {t('readiness_overview')}
                  </h3>
                </div>
                <Chart
                  options={pieChartOptions(
                    "",
                    chartLabels,
                    chartColors
                  )}
                  series={chartSeries}
                  type="pie"
                  height={450}
                  width="100%"
                />              
              </div>
              <div className="flex items-center flex-wrap justify-center gap-x-6 gap-y-2 mt-4">
                {customLegend?.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-sm"
                      style={{ backgroundColor: item?.color }}
                    ></div>
                    <div className="text-sm font-semibold text-slate-700">
                      {item?.label} ({item?.value})
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              {windowConfigs.map((config) => {
                const count = groupedData[config.key]?.length || 0;
                return (
                  <div
                    key={config._id}
                    className={`p-4 rounded-xl border ${config.border} shadow bg-white`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className={`text-lg font-bold ${config?.text}`}>
                          {config.title}
                        </h2>
                        <p className="text-sm text-slate-500">
                          {count} candidates
                        </p>
                        {!config.isPass && (
                          <p className="text-xs text-red-700 mt-1">
                            🚫 Eliminates this candidate
                          </p>
                        )}
                      </div>
                      <div
                        className={`w-12 h-12 rounded-full bg-gradient-to-br ${config.gradient} flex items-center justify-center`}
                      >
                        <i className={`${config.icon} text-white`}></i>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {windowConfigs.map((config) => {
              const employees = groupedData[config.key] || [];

              return (
                <div
                  key={config._id}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200"
                >
                  <div className={`px-6 py-2 bg-slate-800 text-white`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-semibold flex items-center">
                          <i className={`${config.icon} mr-2 text-lg`}></i>
                          {config.title}
                        </h3>
                        <p className="text-white/80 text-sm mt-1">
                          {employees.length} Candidate
                          {employees.length > 1 ? "s" : ""}
                        </p>
                      </div>
                      <div className="text-3xl opacity-10">
                        <i className={config.icon}></i>
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-4">
                    {employees.length > 0 ? (
                      <div className="space-y-3">
                        {employees.map((employee, index) => (
                          <div
                            key={employee._id || index}
                            className={`flex items-center justify-between p-3 ${config.bg} rounded-xl hover:shadow-sm transition-all duration-200 border ${config.border}`}
                          >
                            <div className="flex items-center space-x-3">
                              <div
                                className={`w-2.5 h-2.5 rounded-full bg-gradient-to-r ${config.gradient} shadow`}
                              ></div>
                              <div>
                                <div className="text-sm font-semibold text-slate-800">
                                  {employee.name}
                                </div>
                                <div className="text-xs text-slate-600">
                                  {employee.position}
                                </div>
                              </div>
                            </div>
                            <i
                              className={`${config.icon} ${config.text} text-base opacity-70`}
                            ></i>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-10 text-slate-500">
                        <i
                          className={`${config.icon} text-5xl mb-3 opacity-20`}
                        ></i>
                        <p className="text-base font-medium">
                          This readiness timeframe has no assigned employees.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyReadinessTimeframeReport;
